import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  DEFAULT_PROFILE_AVATAR,
  normalizeAvatarSrc,
} from "../../config/default_avatar";

const Avatar = ({
  src,
  alt = "Avatar",
  size = "md",
  shape = "circle",
  childAvatar = null,
  className = "",
  onClick,
  border = false,
  borderColor = "white",
  rank = 0,
  useRankColors = false,
  online = false,
  onError,
  ...rest
}) => {
  const props = Object.fromEntries(
    Object.entries(rest).filter(([key]) => key !== "icon"),
  );

  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [src]);

  const sizeClasses = {
    xs: "w-6 h-6",
    sm: "w-8 h-8",
    md: "w-12 h-12",
    lg: "w-16 h-16",
    xl: "w-20 h-20",
    "2xl": "w-24 h-24",
    "3xl": "w-32 h-32",
    // Ant Design size aliases used by older call sites
    small: "w-8 h-8",
    large: "w-16 h-16",
    default: "w-12 h-12",
  };

  const childSizeClasses = {
    xs: "w-3 h-3",
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
    xl: "w-8 h-8",
    "2xl": "w-10 h-10",
    "3xl": "w-12 h-12",
    small: "w-4 h-4",
    large: "w-6 h-6",
    default: "w-5 h-5",
  };

  const shapeClasses = {
    circle: "rounded-full",
    square: "rounded-none",
    rounded: "rounded-lg",
  };

  // Pixel sizes from parents that previously used Ant Design Avatar.
  // size="full" fills the parent (e.g. ProfilePicker circle).
  const isNumericSize = typeof size === "number";
  const isFullSize = size === "full";
  const sizeClass = isFullSize
    ? "w-full h-full"
    : isNumericSize
      ? ""
      : sizeClasses[size] || sizeClasses.md;
  const sizeStyle = isNumericSize
    ? { width: size, height: size, minWidth: size, minHeight: size }
    : undefined;

  const childSizeClass = isNumericSize
    ? ""
    : childSizeClasses[size] || childSizeClasses.md;
  const childSizeStyle = isNumericSize
    ? {
        width: Math.max(12, Math.round(size * 0.4)),
        height: Math.max(12, Math.round(size * 0.4)),
      }
    : undefined;

  let actualBorderColor = borderColor;
  if (useRankColors && rank > 0) {
    switch (rank) {
      case 1:
        actualBorderColor = "primary";
        break;
      case 2:
        actualBorderColor = "danger";
        break;
      case 3:
        actualBorderColor = "green";
        break;
      case 4:
        actualBorderColor = "warning";
        break;
      default:
        actualBorderColor = "gray-400";
    }
  }

  const borderClasses = border ? `border-2 border-${actualBorderColor}` : "";

  const avatarClasses = `
    ${sizeClass}
    ${shapeClasses[shape]}
    ${borderClasses}
    object-cover
    bg-gray-200
    flex-shrink-0
    aspect-square
    ${onClick ? "cursor-pointer hover:opacity-80 transition-opacity" : ""}
    ${className}
  `.trim();

  const childAvatarClasses = `
    ${childSizeClass}
    ${shapeClasses[childAvatar?.shape || shape]}
    object-cover
    bg-gray-100
    border-2 border-white
  `.trim();

  // Prefer real photo; otherwise always the global silhouette dummy.
  const normalizedSrc = normalizeAvatarSrc(src);
  const resolvedSrc =
    normalizedSrc && !imageFailed ? normalizedSrc : DEFAULT_PROFILE_AVATAR;

  const handleImageError = (event) => {
    setImageFailed(true);
    onError?.(event);
  };

  return (
    <div
      className={`relative inline-block ${isFullSize ? "w-full h-full" : ""}`}
      {...props}
    >
      <img
        src={resolvedSrc}
        alt={alt}
        className={avatarClasses}
        style={sizeStyle}
        onClick={onClick}
        onError={handleImageError}
      />

      {childAvatar && (
        <div className="absolute -bottom-1 -right-1 z-10">
          {childAvatar.src ? (
            <img
              src={childAvatar.src}
              alt={childAvatar.alt || "Badge"}
              className={childAvatarClasses}
              style={childSizeStyle}
            />
          ) : (
            <img
              src={DEFAULT_PROFILE_AVATAR}
              alt={childAvatar.alt || "Badge"}
              className={childAvatarClasses}
              style={childSizeStyle}
            />
          )}
        </div>
      )}

      {/* Online indicator — only when no child badge occupies that corner */}
      {online && !childAvatar && (
        <span
          className="absolute bottom-0 right-0 z-10 block w-3 h-3 bg-green border-2 border-white rounded-full"
          aria-label="Online"
        />
      )}
    </div>
  );
};

Avatar.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  size: PropTypes.oneOfType([
    PropTypes.oneOf([
      "xs",
      "sm",
      "md",
      "lg",
      "xl",
      "2xl",
      "3xl",
      "full",
      "small",
      "large",
      "default",
    ]),
    PropTypes.number,
  ]),
  shape: PropTypes.oneOf(["circle", "square", "rounded"]),
  childAvatar: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
    shape: PropTypes.oneOf(["circle", "square", "rounded"]),
  }),
  className: PropTypes.string,
  onClick: PropTypes.func,
  border: PropTypes.bool,
  borderColor: PropTypes.string,
  rank: PropTypes.number,
  useRankColors: PropTypes.bool,
  online: PropTypes.bool,
  onError: PropTypes.func,
};

export default Avatar;
